/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelectorToStore } from '@hooks/reduxHooks';
import { useDispatch } from 'react-redux';
import { fetchCalendarData, fetchScheduledClasses } from '@actions/batches';
import ChevronLeft from '@icon/ChevronLeft';
import ChevronRight from '@icon/ChevronRight';
import ScheduleCalendar from '@base/ScheduleCalendar/ScheduleCalendar';
import { Spacings, mediaQuery } from '@styles/index';
import { getBatchByUID, getCalendarData } from '@selectors/batches';
import ItemsList from './ItemsList';

const OuterWrapper = styled.div`
  position: absolute;
  top: 224px;
  right: 0;
  left: 0;
  bottom: 0;
  overflow: hidden;
  ${mediaQuery.uptoMobile} {
    top: 0;
    position: relative;
  }
`;

const ScrollableWrapper = styled.div`
  height: calc(100vh - 224px);
  overflow: scroll;
  ${mediaQuery.uptoMobile} {
    height: auto;
  }
`;

const CalendarWrapper = styled.div`
  position: relative;
  grid-column: span 7;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const InnerWrapper = styled.div`
  width: 1136px;
  margin: auto;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: 8px;
  }
`;

const StyScheduleCalendar = styled(ScheduleCalendar)`
  &.react-calendar {
    top: 230px;
  }
`;
const DaysOfTheWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const formatShortWeekday = (locale, date) => DaysOfTheWeek[date.getDay()];

const BatchScheduleWrapper = ({ batchUID }) => {
  const dispatch = useDispatch();
  const batchData = useSelectorToStore(getBatchByUID(batchUID));
  const calendarData = useSelectorToStore(getCalendarData);
  const currentMonthStartDate = useRef();
  const refDate = new Date();
  refDate.setHours(0, 0, 0, 0);
  const [datePointer, setDatePointer] = useState(refDate.getTime());

  // stroing only month from ref date
  const [currentMonthPointer, setCurrentMonthPointer] = useState(
    refDate.getMonth()
  );
  const goalUID = batchData?.goal?.uid;
  const dispatchFetchScheduleData = (
    url,
    direction,
    callback,
    reset = false
  ) => {
    if (url) {
      dispatch(fetchScheduledClasses(batchUID, url, direction, reset)).then(
        (response) => {
          if (response.result) {
            if (callback) callback(response, direction);
          }
        }
      );
    }
  };
  useEffect(() => {
    const url = `v1/batch/${batchUID}/schedule/?offset=${Math.round(
      datePointer / 1000
    )}&timezone_difference=330&limit=15`;
    dispatchFetchScheduleData(
      url,
      'down',
      (response) => {
        if (response.result)
          dispatchFetchScheduleData(response.result.next, 'down');
      },
      true
    );
    dispatch(fetchCalendarData(batchUID, datePointer));
    window.scrollTo(0, 0);
  }, [dispatch, batchUID]);

  useEffect(() => {
    // If new Date pointer's month is not euqal to current month pointer
    if (new Date(datePointer).getMonth() !== currentMonthPointer) {
      setCurrentMonthPointer(new Date(datePointer).getMonth());
      currentMonthStartDate.current = new Date(datePointer);
      // fetching calendar data if that month is not there
      if (!calendarData[`${batchUID}_${new Date(datePointer).getMonth()}`]) {
        dispatch(fetchCalendarData(batchUID, datePointer));
      }
    }
  }, [datePointer]);
  const isNoClassDay = (date) => {
    if (calendarData[`${batchUID}_${currentMonthPointer}`]) {
      const noClassDay = calendarData[
        `${batchUID}_${currentMonthPointer}`
      ]?.data.filter(
        (item) =>
          Number(item.epoch) === Math.round(new Date(date).getTime() / 1000) &&
          item.count === 0 &&
          Number(item.epoch) !== Math.round(datePointer / 1000)
      );
      return noClassDay.length > 0;
    }
    return false;
  };

  const onDateSelect = (date) => {
    if (isNoClassDay(date)) {
      return;
    }
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    const url = `v1/batch/${batchUID}/schedule/?offset=${Math.round(
      newDate / 1000
    )}&timezone_difference=330&limit=15`;
    dispatch(fetchScheduledClasses(batchUID, url, '', true));
    if (!calendarData[`${batchUID}_${currentMonthPointer}`])
      dispatch(fetchCalendarData(batchUID, newDate.getTime()));
    setDatePointer(newDate.getTime());
  };

  const onMonthNavigationClick = ({ activeStartDate }) => {
    setCurrentMonthPointer(activeStartDate.getMonth());
    currentMonthStartDate.current = activeStartDate;
    if (!calendarData[`${batchUID}_${activeStartDate.getMonth()}`])
      dispatch(fetchCalendarData(batchUID, activeStartDate.getTime()));
  };

  const setClassStylings = ({ date, view }) => {
    let isNoClassDayAttr = false;
    if (
      view === 'month' &&
      calendarData?.[`${batchUID}_${currentMonthPointer}`]
    ) {
      const noClassDay = calendarData[
        `${batchUID}_${currentMonthPointer}`
      ].data.filter(
        (item) =>
          Number(item.epoch) === Math.round(new Date(date).getTime() / 1000) &&
          item.count === 0 &&
          Number(item.epoch) !== Math.round(datePointer / 1000)
      );
      isNoClassDayAttr = noClassDay?.length > 0;
      if (isNoClassDayAttr) {
        return 'disabledClass';
      }
    }
    return null;
  };
  return (
    <OuterWrapper>
      <ScrollableWrapper id="scheduleList">
        <InnerWrapper>
          <ItemsList
            dispatchFetchScheduleData={dispatchFetchScheduleData}
            setDatePointer={setDatePointer}
            datePointer={datePointer}
            goalUID={goalUID}
            batchUID={batchUID}
          />
          <CalendarWrapper>
            <StyScheduleCalendar
              value={new Date(datePointer)}
              defaultActiveStartDate={new Date()}
              activeStartDate={currentMonthStartDate.current}
              locale="en-US"
              calendarType="US"
              formatShortWeekday={formatShortWeekday}
              prevLabel={<ChevronLeft size={Spacings.SPACING_5B} />}
              nextLabel={<ChevronRight size={Spacings.SPACING_5B} />}
              minDetail="month"
              next2Label={null}
              prev2Label={null}
              tileClassName={setClassStylings}
              onClickDay={onDateSelect}
              onActiveStartDateChange={onMonthNavigationClick}
              className="react-calendar-batch"
            />
          </CalendarWrapper>
        </InnerWrapper>
      </ScrollableWrapper>
    </OuterWrapper>
  );
};

export default BatchScheduleWrapper;
BatchScheduleWrapper.propTypes = {
  batchUID: PropTypes.string.isRequired
};
